import React, { useEffect, useState, useRef } from "react";
import { Select, Spin, message } from "antd";
import axiosHttp from "../../appRedux/services/axiosCommon";

const DebouncedCourseSelect = ({
  value,
  onChange,
  placeholder = "Search course by title or keyword...",
  maxCount,
  mode = "multiple",
  style = { width: "100%" },
  merchant,
  extraParams,
}) => {
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Clear non-assigned options when merchant filter changes
  useEffect(() => {
    setOptions((prev) => {
      const assignedIds = Array.isArray(value) ? value : value ? [value] : [];
      return prev.filter((opt) => assignedIds.includes(opt.value));
    });
  }, [merchant]);

  // Fetch initial course names for already assigned IDs
  useEffect(() => {
    const ids = Array.isArray(value) ? value : value ? [value] : [];
    if (ids.length > 0) {
      const promises = ids.map((id) => axiosHttp.get(`/v1/course/${id}`).catch(() => null));
      Promise.all(promises).then((results) => {
        const loadedCourses = results
          .map((res) => res?.data?.results || res?.data?.data || res?.data)
          .filter(Boolean);
        const fetchedOpts = loadedCourses.map((c) => ({
          label: `${c.title || c.name || "Course"} ${
            c.provider || c.merchant || c.platform
              ? `(${c.provider || c.merchant || c.platform})`
              : ""
          }`,
          value: c._id || c.id,
          merchant: c.provider || c.merchant || c.platform || "",
          raw: c,
        }));
        setOptions((prev) => {
          const combined = [...prev, ...fetchedOpts];
          return Array.from(new Map(combined.map((item) => [item.value, item])).values());
        });
      });
    }
  }, [value]);

  const fetchCourses = (query = "") => {
    setFetching(true);
    const params = {
      q: query || undefined,
      title: query || undefined,
      size: 20,
      page: 0,
      ...extraParams,
    };
    if (merchant) {
      params.param = ["merchants"];
      params.value = [merchant];
    }
    axiosHttp
      .get("/v2/courses", {
        params,
      })
      .then((response) => {
        const resData = response.data;
        const raw = Array.isArray(resData)
          ? resData
          : resData?.results?.courses?.data ||
            resData?.results?.courses ||
            resData?.data ||
            resData?.results ||
            resData?.courses ||
            [];
        const courses = Array.isArray(raw) ? raw : [];

        const assignedIds = Array.isArray(value) ? value : value ? [value] : [];
        const existingOptions = options.filter((opt) => assignedIds.includes(opt.value));
        const newOptions = courses.map((c) => ({
          label: `${c.title || c.name} ${
            c.provider || c.merchant || c.platform
              ? `(${c.provider || c.merchant || c.platform})`
              : ""
          }`,
          value: c._id || c.id || c.title,
          merchant: c.provider || c.merchant || c.platform || "",
          raw: c,
        }));

        const combined = [...existingOptions, ...newOptions];
        const uniqueOptions = Array.from(
          new Map(combined.map((item) => [item.value, item])).values()
        );

        setOptions(uniqueOptions);
        setFetching(false);
      })
      .catch(() => {
        setFetching(false);
      });
  };

  const loadOptions = (searchText) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchCourses(searchText);
    }, 300);
  };

  return (
    <Select
      mode={mode === "single" ? undefined : mode}
      value={value}
      placeholder={placeholder}
      onChange={(newVal, option) => {
        if (maxCount && Array.isArray(newVal) && newVal.length > maxCount) {
          message.warning(`You can only assign up to ${maxCount} courses.`);
          newVal = newVal.slice(0, maxCount);
        }
        if (onChange) onChange(newVal, option);
      }}
      style={style}
      showSearch={true}
      filterOption={false}
      onSearch={loadOptions}
      onFocus={() => {
        if (options.length === 0) {
          fetchCourses("");
        }
      }}
      notFoundContent={fetching ? <Spin size="small" /> : "No courses found"}
      options={options}
    />
  );
};

export default DebouncedCourseSelect;
